import { PlansResponse } from '../../types/plans/types';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { analytics } from '../../utils/analytics';
import { get } from '../apiService';

const PLANS_LIST_TTL_MS = 5000;
const plansListCache = new Map<
  string,
  {
    timestamp: number;
    result: { data: PlansResponse | null; loading: boolean };
  }
>();
const plansListInFlight = new Map<
  string,
  Promise<{ data: PlansResponse | null; loading: boolean }>
>();

export const invalidatePlansListCache = (): void => {
  plansListCache.clear();
  plansListInFlight.clear();
};

export const getPlansList = async (
  startToken?: string | null,
  filters?: Record<string, string | string[] | undefined>,
  limit?: number,
): Promise<{ data: PlansResponse | null; loading: boolean }> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer Id is not available');
  }

  // Base query parameters
  const queryParams: Record<string, string> = {
    customerId: customer,
  };

  if (startToken) {
    queryParams.startToken = startToken;
  }

  if (limit) {
    queryParams.limit = limit.toString();
  }

  // Append only non-empty filters
  // Note: filters already contain API keys (marketId, state, etc.)
  if (filters) {
    Object.entries(filters).forEach(([apiKey, value]) => {
      if (value !== undefined && apiKey !== 'undefined') {
        // Special handling for practiceIds, marketId, state, statuses, and
        // payorNames - backend expects multiple parameters when arrays
        if (
          (apiKey === 'practiceIds' ||
            apiKey === 'marketId' ||
            apiKey === 'state' ||
            apiKey === 'statuses' ||
            apiKey === 'payorNames') &&
          Array.isArray(value)
        ) {
          // Don't add to queryParams here, we'll handle it separately
          return;
        }

        // For other filters or single values, use single value (ensure it's a string)
        queryParams[apiKey] = Array.isArray(value) ? value[0] : value;
      }
    });
  }

  // Build URL with query parameters
  const searchParams = new URLSearchParams(queryParams);

  // Add multiple practiceIds parameters if present
  if (filters?.practiceIds && Array.isArray(filters.practiceIds)) {
    filters.practiceIds.forEach((practiceId) => {
      if (practiceId) {
        searchParams.append('practiceIds', practiceId);
      }
    });
  }

  // Add multiple marketId parameters if present (array)
  // Single marketId values are already in queryParams above
  if (filters?.marketId && Array.isArray(filters.marketId)) {
    filters.marketId.forEach((marketId) => {
      if (marketId) {
        searchParams.append('marketId', marketId);
      }
    });
  }

  // Add multiple state parameters if present (array)
  // Single state values are already in queryParams above
  if (filters?.state && Array.isArray(filters.state)) {
    filters.state.forEach((state) => {
      if (state) {
        searchParams.append('state', state);
      }
    });
  }

  // Add multiple statuses parameters if present (array)
  if (filters?.statuses && Array.isArray(filters.statuses)) {
    filters.statuses.forEach((status) => {
      if (status) {
        searchParams.append('statuses', status);
      }
    });
  }

  // Add multiple payorNames parameters if present (array)
  if (filters?.payorNames && Array.isArray(filters.payorNames)) {
    filters.payorNames.forEach((payorName) => {
      if (payorName) {
        searchParams.append('payorNames', payorName);
      }
    });
  }

  const url = `resourceMatrix/insurance/list?${searchParams.toString()}`;

  const cacheKey = url;
  const now = Date.now();
  const cached = plansListCache.get(cacheKey);
  if (cached && now - cached.timestamp < PLANS_LIST_TTL_MS) {
    return cached.result;
  }

  const inFlight = plansListInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  let loading = true;
  const request = (async () => {
    try {
      const responseData = await get(url);
      const data = responseData as PlansResponse;
      loading = false;
      const result = { data, loading };
      plansListCache.set(cacheKey, {
        timestamp: Date.now(),
        result,
      });
      analytics.trackEvent('list', 'plan');
      return result;
    } catch (error) {
      loading = false;
      console.error('Failed to fetch plans list:', error);
      throw new Error('Failed to fetch plans list');
    }
  })();

  plansListInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    plansListInFlight.delete(cacheKey);
  }
};
